import gql from 'graphql-tag';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { client } from '../App';
import { apiCube, AuthorizationCube } from '../utils/index';

export const GET_DETAILS_START = 'GET_DETAILS_START';
export const GET_DETAILS_SUCCESS = 'GET_DETAILS_SUCCESS';
export const GET_DETAILS_FAILURE = 'GET_DETAILS_FAILURE';

export const GET_INDICATORS_DETAILS_START = 'GET_INDICATORS_DETAILS_START';
export const GET_INDICATORS_DETAILS_SUCCESS = 'GET_INDICATORS_DETAILS_SUCCESS';
export const GET_INDICATORS_DETAILS_FAILURE = 'GET_INDICATORS_DETAILS_FAILURE';

const STATUS_COMPLETED = ['Published', 'Finalized'];

const groupByNpo = details => {
  return details.reduce((acc, detail) => {
    const exist = acc.filter(row => row.npo === detail.npo).length > 0;
    const {
      id,
      npo,
      report,
      reach,
      allocation,
      measurement,
      portfolio,
      manager,
      service,
      status,
      lastUpdate,
      ownerEmail,
      relevantResults,
      gResults,
      mResults,
      eResults
    } = detail;
    const completedCount = STATUS_COMPLETED.indexOf(status) > -1 ? 1 : 0;
    if (exist) {
      const index = _.findIndex(acc, { npo });
      acc[index].allocation += allocation;
      acc[index].reach += reach;
      acc[index].measurement += measurement;
      acc[index].reports += 1;
      acc[index].completed += completedCount;
      acc[index].relevantResults += acc[index].relevantResults;
      acc[index].gResults += acc[index].gResults;
      acc[index].mResults += acc[index].mResults;
      acc[index].eResults += acc[index].eResults;
      acc[index].details.push({
        id,
        npo,
        report,
        reach,
        allocation,
        measurement,
        manager,
        portfolio,
        service,
        status,
        lastUpdate,
        ownerEmail,
        relevantResults,
        gResults,
        mResults,
        eResults
      });
    } else {
      acc.push({
        id: uuidv4(),
        open: false,
        npo,
        reach: 0,
        allocation,
        measurement: 0,
        portfolio,
        manager,
        reports: 1,
        completed: completedCount,
        relevantResults,
        gResults,
        mResults,
        eResults,
        details: [
          {
            id,
            npo,
            report,
            reach,
            allocation,
            measurement,
            manager,
            portfolio,
            service,
            status,
            lastUpdate,
            ownerEmail,
            relevantResults,
            gResults,
            mResults,
            eResults
          }
        ]
      });
    }
    return acc;
  }, []);
};

const createDetail = data => {
  const {
    dbId: id,
    name: report,
    grantee: { name: npo },
    reachedClaim: reach,
    totalDonations: allocation,
    dataQualityScore,
    portfolioData,
    serviceName: service,
    status,
    updatedAt: lastUpdate,
    programManager,
    relevantResults,
    gResults,
    mResults,
    eResults
  } = data;
  const ownerEmail = (programManager) ? programManager.ownerEmail : '-';
  const { manager, name: portfolio } = JSON.parse(portfolioData);
  const measurement = dataQualityScore > 1 ? 100 : dataQualityScore * 100;
  const serializedData = {
    id,
    npo,
    report,
    reach,
    allocation,
    measurement,
    portfolio,
    manager,
    service,
    status,
    lastUpdate,
    ownerEmail,
    relevantResults,
    gResults,
    mResults,
    eResults
  };
  return serializedData;
};

const parseDetailsResponse = results => {
  const details = results.data.allReports.edges.map(reportEdge =>
    createDetail(reportEdge.node)
  );
  return _.orderBy(groupByNpo(details), 'allocation', 'desc');
};

const getDetailsStart = () => {
  return {
    type: GET_DETAILS_START,
    payload: null
  };
};

const getDetailsSuccess = (results, isAll) => {
  const list = parseDetailsResponse(results);
  return {
    type: GET_DETAILS_SUCCESS,
    payload: { list, isAll }
  };
};

const getDetailsFailure = error => {
  return {
    type: GET_DETAILS_FAILURE,
    payload: error
  };
};

export const getDetails = ({ 
  accountId,
  portfolio,
  npo,
  service,
  manager,
  report
}) => {
  return dispatch => {
    let filters = `portfolioAccountId: ${accountId}`;
    if (report) {
      filters += `, report: "${report}"`;
    } 
    if (portfolio) {
      filters += `, portfolio: "${portfolio}"`;
    } 
    if (npo) {
      filters += `, npo: "${npo}"`;
    }
    if (service) {
      filters += `, service: "${service}"`;
    }
    if (manager) {
      filters += `, manager: "${manager}"`;
    }
    dispatch(getDetailsStart());
    client
      .query({
        query: gql`
          {
            allReports(filters: {${filters}}) {
              edges {
                node {
                  dbId
                  grantee { name }
                  programManager { email }
                  name
                  reachedClaim
                  totalDonations
                  dataQualityScore
                  portfolioData
                  serviceName
                  status
                  updatedAt
                  relevantResults
                  gResults
                  mResults
                  eResults
                }
              }
            }
          }
        `
      })
      .then(results => dispatch(getDetailsSuccess(results)))
      .catch(error => dispatch(getDetailsFailure(error)));
  };
};

export const getAllDetails = ({ 
  accountId,
}) => {
  return dispatch => {
    dispatch(getDetailsStart());
    client
      .query({
        query: gql`
          {
            allReports(filters: {portfolioAccountId: ${accountId}}) {
              edges {
                node {
                  dbId
                  grantee { name }
                  programManager { email }
                  name
                  reachedClaim
                  totalDonations
                  dataQualityScore
                  portfolioData
                  serviceName
                  status
                  updatedAt
                  relevantResults
                  gResults
                  mResults
                  eResults
                }
              }
            }
          }
        `
      })
      .then(results => dispatch(getDetailsSuccess(results, true)))
      .catch(error => dispatch(getDetailsFailure(error)));
  };
};

const getImpactQuery = indicatorId => {
  const query = {
    measures: [
      'Claims.totalLeveragedClaim',
      'Claims.sroiRow',
      'Claims.leveragePercentage',
      'Claims.costCurrentDirectCash'
    ],
    dimensions: ['Indicators.name', 'Reports.name', 'Accounts.name'],
    filters: [
      {
        dimension: 'Portfolios.status',
        operator: 'equals',
        values: ['Active']
      }
    ]
  };
  if (indicatorId) {
    query.filters.push({
      dimension: 'Indicators.name',
      operator: 'equals',
      values: [indicatorId]
    });
  }
  return encodeURI(JSON.stringify(query));
};

const getIndicatorDetailsStart = () => {
  return {
    type: GET_INDICATORS_DETAILS_START,
    payload: null
  };
};

const getIndicatorDetailsSuccess = (results, indicatorId) => {
  return {
    type: GET_INDICATORS_DETAILS_SUCCESS,
    payload: { results, indicatorId }
  };
};

const getIndicatorDetailsError = error => {
  return {
    type: GET_INDICATORS_DETAILS_FAILURE,
    payload: error
  };
};

export function getIndicatorDetails(indicatorId) {
  if (AuthorizationCube)
    axios.defaults.headers.common.Authorization = AuthorizationCube;
  const impactQuery = getImpactQuery(indicatorId);
  return dispatch => {
    dispatch(getIndicatorDetailsStart());
    return axios
      .get(`${apiCube}cubejs-api/v1/load?query=${impactQuery}`)
      .then(response => response.data)
      .then(data => dispatch(getIndicatorDetailsSuccess(data, indicatorId)))
      .catch(error => dispatch(getIndicatorDetailsError(error)));
  };
}
